import { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent } from "@/js/helpers/tracking";
import { CHAT_TYPES } from "@/js/constants/chat";
import MicroModal from "micromodal";
import isClientSide from "@/js/helpers/isClientSide";
import { extractChatUserId } from "@/hooks/chat/helpers/chatbotHelpers";

export const useChatTracking = ({
  chatButton,
  isVisible,
  chatType,
  lastTriggerFired,
}) => {
  const hasTrackedView = useRef(false);
  const userIdTrackedRef = useRef(false);

  const [detectedUserId, setDetectedUserId] = useState(null);

  const setupTracking = useCallback(() => {
    if (!isVisible) return;

    let retryCount = 0;
    const maxRetries = 20;

    const checkForChatbot = () => {
      if (window?.KoreChatSDK) {
        setTimeout(() => {
          // Start checking for userId after SDK loads
          if (!userIdTrackedRef.current) {
            let checkCount = 0;
            const checkInterval = setInterval(() => {
              const userId = extractChatUserId();
              if (userId && userId !== detectedUserId) {
                setDetectedUserId(userId);
                clearInterval(checkInterval);
              } else if (checkCount > 20) {
                clearInterval(checkInterval);
              }
              checkCount += 1;
            }, 500);
          }
        }, 1000);
      } else if (retryCount < maxRetries) {
        retryCount += 1;
        setTimeout(checkForChatbot, 500);
      } else {
        console?.warn("Chatbot SDK failed to load after maximum retries");
      }
    };

    checkForChatbot();
  }, [isVisible, detectedUserId]);

  // Viewed event for chat in general
  useEffect(() => {
    if (isVisible && !hasTrackedView.current) {
      hasTrackedView.current = true;
      trackEvent({
        action: "elementViewed",
        data: {
          elementType: "CHAT",
          position: `${chatType}${lastTriggerFired.current ? `: ${lastTriggerFired.current} trigger` : ""}`,
        },
      });
    }
  }, [isVisible, chatType, lastTriggerFired]);

  // Clicked event for the primary chat button
  useEffect(() => {
    const chatIcon = chatButton?.querySelector(".chat-circle");
    const handleClick = (e) => {
      if (chatType === CHAT_TYPES.PROACTIVE_V2)
        MicroModal.close("modal-proactive-chat");
      if (!e.isTrusted) return;
      trackEvent({
        action: "elementClicked",
        data: {
          elementType: "BUTTON",
          position: "CHAT",
          text: "Open",
        },
      });
    };

    chatIcon?.addEventListener("click", handleClick);

    return () => {
      chatIcon?.removeEventListener("click", handleClick);
    };
  }, [chatButton]);

  // Clicked events for all buttons within the chat window
  // Note: does not include the primary chat button because it doesn't use a standard button element
  useEffect(() => {
    const subButtons = chatButton?.querySelectorAll("button");
    const handlers = new Map();

    subButtons?.forEach((button) => {
      const handleClick = () => {
        trackEvent({
          action: "elementClicked",
          data: {
            elementType: "BUTTON",
            position: "CHAT",
            text: button?.title || button?.textContent?.trim() || "unknown",
          },
        });
      };

      handlers.set(button, handleClick);
      button.addEventListener("click", handleClick);
    });

    return () => {
      handlers.forEach((handler, button) => {
        button.removeEventListener("click", handler);
      });
    };
  }, [chatButton]);

  // Focus and input events for the chat text input
  useEffect(() => {
    const chatTextInput = chatButton?.querySelector('[role="textbox"]');

    const handleInputFocused = () => {
      trackEvent({
        action: "elementClicked",
        data: {
          elementType: "INPUT",
          position: "CHAT",
          text: "Message input",
        },
      });
    };

    const handleUserTyped = () => {
      trackEvent({
        action: "fieldInputted",
        data: {
          elementType: "INPUT",
          position: "CHAT",
          text: "Message input",
        },
      });
    };

    chatTextInput?.addEventListener("focus", handleInputFocused);
    chatTextInput?.addEventListener("input", handleUserTyped);
    return () => {
      chatTextInput?.removeEventListener("focus", handleInputFocused);
      chatTextInput?.removeEventListener("input", handleUserTyped);
    };
  }, [chatButton]);

  const userId =
    detectedUserId ||
    (isClientSide() && isVisible ? extractChatUserId() : null);

  useEffect(() => {
    setupTracking();
  }, [setupTracking]);

  useEffect(() => {
    if (!userId || userIdTrackedRef.current) return;

    trackEvent({
      action: "chatIdentified",
      data: {
        chatUserId: userId,
      },
    });
    userIdTrackedRef.current = true;
  }, [userId]);
};
